import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Scale,
  Plus,
  Search,
  Filter,
  Edit,
  Power,
  Trash2,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Copy,
  MoreHorizontal,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import type { DisputeType, DisputeTypeFilters } from '@/types/dispute-types';
import {
  useDisputeTypesList,
  useDuplicateDisputeType,
  useDeleteDisputeType,
  useToggleDisputeTypeStatus,
} from '@/hooks/useDisputeTypesData';
import { ApiError } from '@/providers/ApiConfigProvider';

// Helper function to check if dispute type is fully configured
// Only applies to System types - Manual types are always considered complete
const isDisputeTypeFullyConfigured = (disputeType: DisputeType): boolean => {
  // Manual dispute types are always considered fully configured
  if (!disputeType.isSystemType) {
    return true;
  }

  // For System types, check if workflow is configured
  if (!disputeType.workflow?.stages || disputeType.workflow.stages.length === 0) {
    return false;
  }
  
  // Check if all stages (except the last "Resolved" stage) have assigned users and templates
  // The last stage typically doesn't need SLA configuration
  const stagesToCheck = disputeType.workflow.stages.filter(stage => stage.properties?.slaHours);
  
  return stagesToCheck.every(stage => {
    const hasAssignedUser = stage.properties?.allowedAssigneeRoles && 
                           stage.properties.allowedAssigneeRoles.length > 0 &&
                           stage.properties.allowedAssigneeRoles[0] !== 'Unassigned';
    const hasTemplate = (stage.properties as any)?.documentTemplate && 
                       (stage.properties as any).documentTemplate !== 'None';
    return hasAssignedUser && hasTemplate;
  });
};

// Helper to render delete dialog description based on dispute type state
function renderDeleteDialogContent(disputeType: DisputeType | null): React.ReactNode {
  if (disputeType?.isSystemType) {
    return (
      <>
        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
          <AlertCircle className="h-4 w-4" />
          <span className="font-medium">Cannot delete system dispute type</span>
        </div>
        <p>
          This is a system dispute type and cannot be deleted. You can deactivate it or edit its configuration instead.
        </p>
      </>
    );
  }
  if (disputeType?.usageCount && disputeType.usageCount > 0) {
    return (
      <>
        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
          <AlertCircle className="h-4 w-4" />
          <span className="font-medium">Cannot delete this dispute type</span>
        </div>
        <p>
          This dispute type has {disputeType.usageCount} active dispute(s) associated with it.
          Please deactivate it instead or reassign the disputes to another type.
        </p>
      </>
    );
  }
  return (
    <>
      Are you sure you want to delete &quot;{disputeType?.name}&quot;? This action cannot be undone.
    </>
  );
}

const DisputeTypesPage: React.FC = () => {
  const navigate = useNavigate();

  const { data: disputeTypes = [], isLoading, isError, error } = useDisputeTypesList();
  const duplicateDisputeTypeMutation = useDuplicateDisputeType();
  const deleteDisputeTypeMutation = useDeleteDisputeType();
  const toggleStatusMutation = useToggleDisputeTypeStatus();

  const [filters, setFilters] = useState<DisputeTypeFilters>({
    search: '',
    status: 'all',
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [disputeTypeToDelete, setDisputeTypeToDelete] = useState<DisputeType | null>(null);

  // Calculate statistics
  const stats = useMemo(() => {
    const activeTypes = disputeTypes.filter(dt => dt.isActive);

    return {
      totalTypes: disputeTypes.length,
      activeTypes: activeTypes.length,
    };
  }, [disputeTypes]);

  // Filter and sort dispute types
  const filteredDisputeTypes = useMemo(() => {
    let filtered = [...disputeTypes];

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(dt => 
        filters.status === 'active' ? dt.isActive : !dt.isActive
      );
    }

    // Apply search filter
    if (filters.search) {
      const query = filters.search.toLowerCase();
      filtered = filtered.filter(dt =>
        dt.name.toLowerCase().includes(query) ||
        dt.description.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'updated':
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }

      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [disputeTypes, filters]);

  const handleCreateNew = () => {
    navigate('/settings/dispute-types/create');
  };

  const handleEdit = (disputeType: DisputeType) => {
    navigate(`/settings/dispute-types/${disputeType.id}/workflow`);
  };

  const handleDuplicate = (disputeType: DisputeType) => {
    duplicateDisputeTypeMutation.mutate(
      {
        sourceId: disputeType.id,
        newName: `${disputeType.name} (Copy)`,
      },
      {
        onSuccess: () => {
          toast.success('Dispute type duplicated successfully');
        },
        onError: (err) => {
          toast.error(`Failed to duplicate dispute type: ${err.message}`);
        },
      }
    );
  };

  const handleToggleStatus = (disputeType: DisputeType) => {
    // Prevent activation if not fully configured
    if (!disputeType.isActive && !isDisputeTypeFullyConfigured(disputeType)) {
      toast.error(
        'Cannot activate dispute type. Please configure all workflow levels with assigned users and email templates first.',
        { duration: 5000 }
      );
      return;
    }

    toggleStatusMutation.mutate(
      { id: disputeType.id, isActive: !disputeType.isActive },
      {
        onSuccess: () => {
          toast.success(`Dispute type ${disputeType.isActive ? 'deactivated' : 'activated'} successfully`);
        },
        onError: (err) => {
          toast.error(`Failed to update dispute type status: ${err.message}`);
        },
      }
    );
  };

  const handleDeleteClick = (disputeType: DisputeType) => {
    setDisputeTypeToDelete(disputeType);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (disputeTypeToDelete) {
      if (disputeTypeToDelete.usageCount > 0) {
        toast.error('Cannot delete dispute type with active disputes. Please deactivate it instead.');
        setIsDeleteDialogOpen(false);
        setDisputeTypeToDelete(null);
      } else {
        deleteDisputeTypeMutation.mutate(disputeTypeToDelete.id, {
          onSuccess: () => {
            toast.success('Dispute type deleted successfully');
            setIsDeleteDialogOpen(false);
            setDisputeTypeToDelete(null);
          },
          onError: (err) => {
            const isConflict = err instanceof ApiError && err.statusCode === 409;
            const errorMessage = isConflict
              ? 'This dispute type is in use and cannot be deleted. Please deactivate it instead.'
              : `Failed to delete dispute type: ${err.message}`;
            toast.error(errorMessage);
            setIsDeleteDialogOpen(false);
            setDisputeTypeToDelete(null);
          },
        });
      }
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading dispute types...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-lg font-semibold">Failed to load dispute types</h2>
        <p className="text-muted-foreground mt-1">
          {error?.message || 'An unexpected error occurred'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Scale className="h-8 w-8" />
            Dispute Types
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure dispute types and customise workflow stages for each type
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          Create Dispute Type
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Types</CardTitle>
            <Scale className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalTypes}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.activeTypes} active
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Types</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeTypes}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Currently enabled
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search dispute types..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9"
          />
        </div>
        <Select
          value={filters.status}
          onValueChange={(value: 'all' | 'active' | 'inactive') => setFilters({ ...filters, status: value })}
        >
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.sortBy}
          onValueChange={(value: 'name' | 'created' | 'updated') => setFilters({ ...filters, sortBy: value })}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="created">Created Date</SelectItem>
            <SelectItem value="updated">Updated Date</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setFilters({ ...filters, sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })}
        >
          <TrendingUp className={`h-4 w-4 transition-transform ${filters.sortOrder === 'desc' ? 'rotate-180' : ''}`} />
        </Button>
      </div>

      {/* Dispute Types Table */}
      <Card>
        <CardHeader>
          <CardTitle>Dispute Types ({filteredDisputeTypes.length})</CardTitle>
          <CardDescription>
            Manage dispute type configurations and workflows
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name & Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Workflow Levels</TableHead>
                <TableHead>Setup Status</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDisputeTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No dispute types found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredDisputeTypes.map((disputeType) => (
                  <TableRow key={disputeType.id}>
                    <TableCell>
                      <div>
                        <button
                          onClick={() => handleEdit(disputeType)}
                          className="font-medium text-primary hover:underline text-left"
                        >
                          {disputeType.name}
                        </button>
                        <div className="text-sm text-muted-foreground max-w-md truncate">
                          {disputeType.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {disputeType.isSystemType ? (
                        <Badge variant="outline" className="text-xs">
                          System
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          Manual
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {!disputeType.isActive && !isDisputeTypeFullyConfigured(disputeType) ? (
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div>
                                  <Switch
                                    checked={disputeType.isActive}
                                    onCheckedChange={() => handleToggleStatus(disputeType)}
                                    disabled={true}
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Complete the setup before activating this dispute type</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <Switch
                            checked={disputeType.isActive}
                            onCheckedChange={() => handleToggleStatus(disputeType)}
                            disabled={false}
                          />
                        )}
                        <span className="text-sm">
                          {disputeType.isActive ? (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                        {disputeType.workflow.stages.length} levels
                      </div>
                    </TableCell>
                    <TableCell>
                      {isDisputeTypeFullyConfigured(disputeType) ? (
                        <Badge variant="outline" className="text-green-700 border-green-700 bg-green-50">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Complete
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-yellow-700 border-yellow-600 bg-yellow-50">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Incomplete
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {format(new Date(disputeType.updatedAt), 'MMM dd, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(disputeType)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Dispute Type
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(disputeType)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleToggleStatus(disputeType)}>
                            <Power className="h-4 w-4 mr-2" />
                            {disputeType.isActive ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteClick(disputeType)}
                            className="text-destructive"
                            disabled={disputeType.usageCount > 0 || disputeType.isSystemType}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Dispute Type</AlertDialogTitle>
            <AlertDialogDescription>
              {renderDeleteDialogContent(disputeTypeToDelete)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {!disputeTypeToDelete?.isSystemType && (!disputeTypeToDelete?.usageCount || disputeTypeToDelete.usageCount === 0) && (
              <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DisputeTypesPage;
